/**
 * The library: every report once, with what needs a person at the top.
 *
 * Left, the collections. Middle, the reports, each with its last run, its next
 * run and a fortnight of activity. Right, the selected report. Every number on
 * this screen is read from a record the server holds; where there is nothing to
 * count, it says nothing.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Star } from 'lucide-react'
import clsx from 'clsx'
import DialogShell from '@/components/crm/DialogShell'
import { TYPE_BUCKETS } from './catalog'
import { formatBytes, formatWhen, plural } from './format'
import {
  LIBRARY_COLLECTIONS,
  collectionCounts,
  filterLibrary,
  libraryTotals,
  type CollectionId,
  type LibraryReport,
  type LibrarySort,
} from './library'
import { isFavorite } from './favorites'
import { ReportPanel, type ReportHandlers } from './ReportPanel'
import type { ReportingTab } from './tabs'
import { RunsList } from './RunsList'
import { Chip, EmptyState, ErrorBanner, Eyebrow, FOCUS, Panel, Pill, SkeletonRows } from './ui'
import type { ReportActions } from './useReportActions'
import type { ReportingData, ReportPermissions } from './useReportingData'


type Props = {
  data: ReportingData
  actions: ReportActions
  perms: ReportPermissions
  handlers: ReportHandlers
  query: string
  collection: CollectionId
  onCollection: (collection: CollectionId) => void
  sort: LibrarySort
  onSort: (sort: LibrarySort) => void
  selectedKey: string | null
  onSelect: (key: string) => void
  favorites: ReadonlySet<string>
  onToggleFavorite: (report: LibraryReport) => void
  docked: boolean
  onGoTab: (tab: ReportingTab, options?: { deliveryFilter?: 'failed' }) => void
}

// ---------------------------------------------------------------------------
// The strip at the top
// ---------------------------------------------------------------------------

function StripCard({
  eyebrow,
  label,
  value,
  valueTone,
  children,
}: {
  eyebrow: string
  label: string
  /** The one number the card is about, set large beside its sentence. */
  value?: ReactNode
  valueTone?: 'bad' | 'gold'
  children: ReactNode
}) {
  return (
    <Panel as="section" aria-label={label} className="flex min-w-0 flex-col gap-1.5 px-4 py-3">
      <Eyebrow>{eyebrow}</Eyebrow>
      <div className="flex items-start gap-3">
        {value !== undefined ? (
          <div
            className={clsx(
              'min-w-[28px] text-[28px] font-semibold leading-[1.05] tabular-nums',
              valueTone === 'bad' ? 'text-[#F0B08A]' : valueTone === 'gold' ? 'text-[#FFD86B]' : 'text-[#ECEAE4]',
            )}
          >
            {value}
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
      </div>
    </Panel>
  )
}

function LinkButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx('mt-auto self-start rounded text-left text-[13px] font-semibold text-[#FFD86B] hover:text-[#FFE9A8]', FOCUS)}
    >
      {children}
    </button>
  )
}

function AttentionStrip({ data, onGoTab }: { data: ReportingData; onGoTab: Props['onGoTab'] }) {
  const { attention, status, statusFailed, schedules } = data
  const deployment = attention.deployment
  const issues = attention.schedules
  const issueParts = [
    issues.overdue.length ? `${issues.overdue.length} overdue` : null,
    issues.noNextRun.length ? `${issues.noNextRun.length} without a next run` : null,
    issues.drafts.length ? `${plural(issues.drafts.length, 'draft')} never run` : null,
  ].filter(Boolean)
  const failed = attention.failures

  return (
    <section aria-label="Needs attention" className="flex flex-col gap-3">
      <Panel
        as="section"
        aria-label="Delivery"
        tone={deployment?.tone === 'ok' ? 'plain' : 'gold'}
        className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3"
      >
        {deployment ? (
          <>
            <div className="min-w-[240px] flex-[1_1_300px]">
              <Eyebrow tone="gold">{deployment.eyebrow}</Eyebrow>
              <div className="mt-1 font-display text-[20px] font-semibold leading-[1.1] text-[#F5F3EE]">{deployment.title}</div>
            </div>
            <p className="min-w-[220px] flex-[1.2_1_260px] text-[12.5px] text-[#B9B9B9]">{deployment.body}</p>
            <LinkButton onClick={() => onGoTab('schedules')}>
              {deployment.tone === 'ok' ? 'See every schedule' : 'See what is blocking each schedule'}
            </LinkButton>
          </>
        ) : (
          <>
            <Eyebrow tone="gold">DEPLOYMENT</Eyebrow>
            <p className={clsx('text-[13px]', statusFailed ? 'text-[#F0B08A]' : 'text-[#8F8F8F]')}>
              {statusFailed
                ? 'This deployment’s delivery switches could not be read, so the page cannot say whether anything is emailed.'
                : status
                  ? ''
                  : 'Reading this deployment’s delivery switches…'}
            </p>
          </>
        )}
      </Panel>

      <div className="grid gap-3 sm:grid-cols-3">
        <StripCard
          eyebrow="FAILED"
          label="Failed in the last 7 days"
          value={data.deliveryError ? undefined : data.delivery ? (failed.complete ? failed.total : `${failed.total}+`) : '—'}
          valueTone={failed.total ? 'bad' : undefined}
        >
          {data.deliveryError ? (
            <p className="text-[12.5px] text-[#F0B08A]">The delivery ledger could not be read, so failures cannot be counted.</p>
          ) : (
            <>
              <p className="text-[12.5px] text-[#9A9A9A]">
                {data.delivery
                  ? failed.total
                    ? `${failed.complete ? 'Failed' : 'At least'} in the last 7 days: ${plural(failed.runs, 'run')}, ${plural(failed.deliveries, 'delivery', 'deliveries')}.${failed.complete ? '' : ' More runs happened in that time than this page reads.'}`
                    : failed.complete
                      ? 'No scheduled run or delivery failed in the last 7 days.'
                      : 'None in the latest runs this page reads. More runs happened in the last 7 days than it covers, so earlier ones are not counted.'
                  : 'Reading the delivery ledger…'}
              </p>
              {failed.total ? <LinkButton onClick={() => onGoTab('deliveries', { deliveryFilter: 'failed' })}>Show them</LinkButton> : null}
            </>
          )}
        </StripCard>

        <StripCard
          eyebrow="SCHEDULES"
          label="Schedules that need action"
          value={issues.needingAction.length}
          valueTone={issues.needingAction.length ? 'gold' : undefined}
        >
          <p className="text-[12.5px] text-[#9A9A9A]">
            {issues.needingAction.length
              ? `${issueParts.join(', ')}.`
              : schedules.length
                ? 'Every schedule is on track or paused on purpose.'
                : 'There are no schedules yet.'}
          </p>
          {issues.needingAction.length ? <LinkButton onClick={() => onGoTab('schedules')}>Review schedules</LinkButton> : null}
        </StripCard>

        <StripCard eyebrow="NEXT RUN" label="Next run">
          {attention.nextRun.kind === 'due' ? (
            <>
              <div className="truncate text-[15px] font-semibold leading-tight text-[#F5F3EE]" title={attention.nextRun.scheduleName}>
                {attention.nextRun.scheduleName}
              </div>
              <p className="text-[12.5px] text-[#9A9A9A]">{attention.nextRun.text}</p>
            </>
          ) : (
            <p className="text-[12.5px] text-[#9A9A9A]">{data.loading ? 'Reading schedules…' : attention.nextRun.reason}</p>
          )}
        </StripCard>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function ActivityBars({ report }: { report: LibraryReport }) {
  const runs = report.activity.reduce((sum, day) => sum + day.runs, 0)
  const failures = report.activity.reduce((sum, day) => sum + day.failures, 0)
  const label = `Last ${report.activity.length} days: ${plural(runs, 'run')}${failures ? `, ${plural(failures, 'failure')}` : ''}`
  return (
    <span role="img" aria-label={label} title={label} className="flex h-[26px] w-[84px] shrink-0 items-end gap-[2px]">
      {report.activity.map((day) => (
        <span
          key={day.day}
          aria-hidden
          className={clsx(
            'w-[4px] rounded-[1px]',
            day.failures ? 'bg-[#E8915C]' : day.runs ? 'bg-[#9A9A9A]' : 'bg-[#262626]',
          )}
          style={{ height: day.failures || day.runs ? 22 : 4 }}
        />
      ))}
    </span>
  )
}

function LibraryRow({
  report,
  selected,
  open,
  onToggleRuns,
  onSelect,
  onOpenPage,
  favorite,
  onToggleFavorite,
  data,
  actions,
  perms,
}: {
  report: LibraryReport
  selected: boolean
  open: boolean
  onToggleRuns: () => void
  onSelect: () => void
  onOpenPage: () => void
  favorite: boolean
  onToggleFavorite: () => void
  data: ReportingData
  actions: ReportActions
  perms: ReportPermissions
}) {
  const now = data.now
  const next =
    report.nextRun?.text ??
    report.health.find((entry) => entry.health.state !== 'healthy')?.health.nextRunText ??
    (report.schedules.length ? '—' : 'Not scheduled')
  const lastLine = !report.last
    ? report.schedules.length
      ? 'Not run yet'
      : 'Never run'
    : report.last.ok
      ? report.runs[0]
        ? `Generated · ${formatBytes(report.runs[0].sizeBytes)}`
        : 'Generated'
      : 'Failed'

  return (
    <li>
      <Panel
        as="article"
        tone={selected ? 'gold' : 'plain'}
        className={clsx('cursor-pointer overflow-hidden', selected && 'border-[#6B5A27]')}
        data-testid="library-row"
        onClick={onSelect}
        onDoubleClick={onOpenPage}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 lg:grid lg:[grid-template-columns:34px_minmax(0,1fr)_124px_136px_84px]">
          <span
            aria-hidden
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-[#2A2A2A] bg-[#141414] text-[10px] tracking-[.06em] text-[#FFD86B]"
          >
            {report.bucket.code}
          </span>
          <span className="flex min-w-0 flex-[1_1_200px] flex-col gap-0.5">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onSelect()
              }}
              aria-current={selected ? 'true' : undefined}
              title={report.name}
              className={clsx('max-w-full self-start truncate rounded text-left text-[14px] font-semibold text-[#F5F3EE] hover:text-white', FOCUS)}
            >
              {report.name}
            </button>
            <span className="line-clamp-2 text-[12px] text-[#8F8F8F]">
              {[report.bucket.name, report.scope, report.version ? `v${report.version}` : null, report.owner, report.sharing]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          <span className="flex w-[124px] shrink-0 flex-col gap-0.5 lg:w-auto">
            <span className="text-[13px] text-[#ECEAE4]">{report.last ? formatWhen(report.last.at, { now }) : '—'}</span>
            <span className={clsx('text-[12px]', report.last && !report.last.ok ? 'text-[#F0B08A]' : 'text-[#8F8F8F]')}>{lastLine}</span>
          </span>
          <span className="flex w-[136px] shrink-0 flex-col gap-0.5 lg:w-auto">
            <span className="text-[13px] text-[#ECEAE4]">{next}</span>
            <span className="text-[12px] text-[#8F8F8F]">
              {report.recipientCount ? plural(report.recipientCount, 'recipient') : 'No recipients'}
            </span>
          </span>
          <ActivityBars report={report} />
        </div>

        {report.flag ? (
          <div
            className={clsx(
              'mx-4 mb-3 rounded-lg border px-3 py-2 text-[12.5px]',
              report.flag.tone === 'bad'
                ? 'border-[#4A3220] bg-[#140E0A] text-[#F0B08A]'
                : 'border-[#4A3F1E] bg-[#12100A] text-[#E8D9A8]',
            )}
          >
            {report.flag.text}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={(event) => {
              // Opening a report's runs is not choosing the report: on a narrow screen
              // choosing it opens a sheet over the page.
              event.stopPropagation()
              onToggleRuns()
            }}
            aria-expanded={open}
            disabled={report.runs.length + report.failures.length === 0}
            className={clsx(
              'inline-flex items-center gap-1 rounded-md border border-[#262626] px-2.5 py-0.5 text-[12px] text-[#C9C9C9] hover:bg-[#171717] disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent',
              FOCUS,
            )}
          >
            {open ? <ChevronDown className="h-3 w-3" aria-hidden /> : <ChevronRight className="h-3 w-3" aria-hidden />}
            {report.runs.length === 0
              ? 'No runs yet'
              : open
                ? 'Hide runs'
                : `Show ${plural(report.runs.length, 'run')}`}
          </button>
          <span className="text-[12px] text-[#8C8C8C]">{report.formats}</span>
          {report.health.map(({ schedule, health }) => (
            <Pill key={schedule.id} tone={health.tone}>
              {health.label}
            </Pill>
          ))}
          <button
            type="button"
            aria-pressed={favorite}
            aria-label={favorite ? `Remove ${report.name} from favorites` : `Favorite ${report.name}`}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            onClick={(event) => {
              event.stopPropagation()
              onToggleFavorite()
            }}
            className={clsx(
              'ml-auto rounded-full p-1.5 transition-colors hover:bg-[#171717]',
              FOCUS,
              favorite ? 'text-[#FFD86B]' : 'text-[#8C8C8C] hover:text-[#FFD86B]',
            )}
          >
            <Star className="h-3.5 w-3.5" fill={favorite ? 'currentColor' : 'none'} aria-hidden />
          </button>
        </div>

        {open ? (
          <div className="cursor-default border-t border-[#1B1B1B] px-4 pb-3 pt-1" onClick={(event) => event.stopPropagation()}>
            <RunsList report={report} actions={actions} perms={perms} now={now} initiallyShown={5} />
          </div>
        ) : null}
      </Panel>
    </li>
  )
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

function collectionLabel(id: CollectionId): string {
  const fixed = LIBRARY_COLLECTIONS.find((entry) => entry.id === id)
  if (fixed) return fixed.label
  return TYPE_BUCKETS.find((bucket) => `type:${bucket.key}` === id)?.name ?? 'Reports'
}

export function LibraryTab({
  data,
  actions,
  perms,
  handlers,
  query,
  collection,
  onCollection,
  sort,
  onSort,
  selectedKey,
  onSelect,
  favorites,
  onToggleFavorite,
  docked,
  onGoTab,
}: Props) {
  const [openRuns, setOpenRuns] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const counts = useMemo(() => collectionCounts(data.library, favorites), [data.library, favorites])
  const totals = useMemo(() => libraryTotals(data.library), [data.library])
  const visible = useMemo(
    () => filterLibrary(data.library, { collection, query, sort, favorites }),
    [data.library, collection, query, sort, favorites],
  )
  const selected = data.library.find((report) => report.key === selectedKey) ?? null

  // The selection follows the list: if what was selected is filtered away or deleted, take the first row.
  useEffect(() => {
    if (!visible.length) return
    if (!selectedKey || !visible.some((report) => report.key === selectedKey)) onSelect(visible[0].key)
  }, [visible, selectedKey, onSelect])

  const collectionButton = (id: CollectionId, label: string, code?: string) => {
    const active = collection === id
    const count = counts[id] ?? 0
    return (
      <button
        key={id}
        type="button"
        aria-current={active ? 'true' : undefined}
        onClick={() => onCollection(id)}
        className={clsx(
          'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors',
          FOCUS,
          active ? 'bg-[#1A1A1A] font-semibold text-white' : 'text-[#C9C9C9] hover:bg-[#171717]',
        )}
      >
        <span className="flex items-center gap-2">
          {code ? <span className="w-[30px] text-[10px] tracking-[.08em] text-[#FFD86B]">{code}</span> : null}
          <span>{label}</span>
        </span>
        <span className="tabular-nums text-[#8F8F8F]">{count}</span>
      </button>
    )
  }

  const rail = (
    <nav aria-label="Collections" className={clsx(docked ? 'flex flex-col gap-5' : 'flex flex-wrap gap-1.5')}>
      {docked ? (
        <>
          <div>
            <Eyebrow className="px-2.5 pb-2">LIBRARY</Eyebrow>
            <div className="flex flex-col gap-0.5">
              {LIBRARY_COLLECTIONS.map((entry) => collectionButton(entry.id, entry.label))}
            </div>
          </div>
          <div>
            <Eyebrow className="px-2.5 pb-2">BY TYPE</Eyebrow>
            <div className="flex flex-col gap-0.5">
              {TYPE_BUCKETS.filter((bucket) => counts[`type:${bucket.key}`]).map((bucket) =>
                collectionButton(`type:${bucket.key}` as CollectionId, bucket.name, bucket.code),
              )}
            </div>
          </div>
          <Panel className="p-3 text-[12px] text-[#8F8F8F]">
            <div className="mb-1 font-medium text-[#D8D8D8]">
              {plural(totals.files, 'file')}, {plural(totals.reports, 'report')}
            </div>
            Each report is listed once. Its generated files sit under it as runs.
            {data.historyTruncated ? ' Only the latest files are read, so older ones are not counted.' : ''}
          </Panel>
        </>
      ) : (
        [
          ...LIBRARY_COLLECTIONS.map((entry) => ({ id: entry.id, label: entry.label })),
          ...TYPE_BUCKETS.filter((bucket) => counts[`type:${bucket.key}`]).map((bucket) => ({
            id: `type:${bucket.key}` as CollectionId,
            label: bucket.name,
          })),
        ].map((entry) => (
          <Chip key={entry.id} active={collection === entry.id} onClick={() => onCollection(entry.id)}>
            {entry.label} <span className="tabular-nums text-[#8F8F8F]">{counts[entry.id] ?? 0}</span>
          </Chip>
        ))
      )}
    </nav>
  )

  const list = (
    <div className="flex min-w-0 flex-col gap-5">
      <AttentionStrip data={data} onGoTab={onGoTab} />

      {data.failures.map((failure) => (
        <ErrorBanner key={failure.key} onRetry={failure.retry}>
          Could not read {failure.what}, so what you see below is incomplete.
        </ErrorBanner>
      ))}

      <section aria-label="Library controls" className="flex flex-wrap items-center gap-3">
        <div role="status" className="mr-auto text-[13px] text-[#9A9A9A]">
          {data.loading
            ? 'Loading reports…'
            : `${visible.length} of ${data.library.length} ${data.library.length === 1 ? 'report' : 'reports'} shown${
                collection !== 'all' ? ` in ${collectionLabel(collection)}` : ''
              }${query.trim() ? ` matching “${query.trim()}”` : ''}`}
        </div>
        <div className="flex gap-1.5" role="group" aria-label="Sort reports">
          <Chip active={sort === 'recent'} onClick={() => onSort('recent')}>
            Recently run
          </Chip>
          <Chip active={sort === 'name'} onClick={() => onSort('name')}>
            Name
          </Chip>
        </div>
      </section>

      <section aria-label="Reports">
        {data.loading ? (
          <SkeletonRows label="Loading reports" />
        ) : visible.length === 0 ? (
          data.library.length === 0 ? (
            <EmptyState
              title="No reports yet"
              body="Create a report and it is listed here with its runs, its schedule and its delivery record."
            />
          ) : (
            <EmptyState
              title="No reports match"
              body={query.trim() ? 'Clear the search or pick another collection.' : 'Nothing is in this collection right now.'}
            />
          )
        ) : (
          <ul className="flex flex-col gap-2.5">
            {visible.map((report) => (
              <LibraryRow
                key={report.key}
                report={report}
                selected={selectedKey === report.key}
                open={openRuns === report.key}
                onToggleRuns={() => setOpenRuns((current) => (current === report.key ? null : report.key))}
                onSelect={() => {
                  onSelect(report.key)
                  if (!docked) setSheetOpen(true)
                }}
                onOpenPage={() => handlers.detail(report)}
                favorite={isFavorite(report, favorites)}
                onToggleFavorite={() => onToggleFavorite(report)}
                data={data}
                actions={actions}
                perms={perms}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  )

  // In the sheet (below the docked width) every hand-off leaves the sheet first: the editor, the
  // schedule dialog and the share dialog open on their own layer, and a sheet left open would sit
  // above them and make them unreachable.
  const panelHandlers: ReportHandlers = docked
    ? handlers
    : {
        open: (report) => {
          setSheetOpen(false)
          handlers.open(report)
        },
        schedule: (report) => {
          setSheetOpen(false)
          handlers.schedule(report)
        },
        editSchedule: (schedule) => {
          setSheetOpen(false)
          handlers.editSchedule(schedule)
        },
        share: (report) => {
          setSheetOpen(false)
          handlers.share(report)
        },
        detail: (report) => {
          setSheetOpen(false)
          handlers.detail(report)
        },
      }

  const panel = selected ? (
    <ReportPanel
      report={selected}
      actions={actions}
      perms={perms}
      handlers={panelHandlers}
      me={data.me}
      now={data.now}
      idPrefix={docked ? 'panel' : 'sheet'}
    />
  ) : (
    <p className="text-[13px] text-[#8F8F8F]">Select a report to see its latest file, its schedule and its runs.</p>
  )

  return docked ? (
    <div className="grid min-h-0 flex-1 grid-cols-[184px_minmax(0,1fr)_350px] gap-0">
      <div className="min-h-0 overflow-y-auto border-r border-[#1E1E1E] bg-[#080808] px-3 py-5">{rail}</div>
      <main className="min-h-0 min-w-0 overflow-y-auto px-6 pb-10 pt-5" aria-label="Reports">
        {list}
      </main>
      <aside
        aria-label="Selected report"
        className="flex min-h-0 flex-col overflow-y-auto border-l border-[#1E1E1E] bg-[#080808] px-5 py-5"
      >
        {panel}
      </aside>
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="border-b border-[#1E1E1E] bg-[#080808] px-4 py-3">{rail}</div>
      <main className="min-w-0 px-4 pb-10 pt-5" aria-label="Reports">
        {list}
      </main>
      {sheetOpen && selected ? (
        <DialogShell label={selected.name} onClose={() => setSheetOpen(false)} width="lg" testId="report-sheet">
          <div className="max-h-[78vh] overflow-y-auto p-4">{panel}</div>
        </DialogShell>
      ) : null}
    </div>
  )
}
